/**
 * Machines on their tiles, by type and level (models/machines.ts): each stands in its tile's
 * middle, turned a little by the tile's own value. Its core fills to how full it is, followed over
 * time without rebuilding anything, and its turning part turns while it works, until it is full.
 */
import * as THREE from 'three';

import { hash2 } from '@core/math';

import { lookOf } from '../models/machines';
import type { MachinePart } from '../models/part';
import type { MachineView, PlanetView } from '../planet/view';
import { centerOf, UP } from './geometry';
import type { Instances } from './instances';
import type { Spin, Spinners } from './spinners';

/** A core at nothing still shows a sliver: an empty extractor isn't a broken one. */
const EMPTY = 0.02;

interface Fill {
  tile: number;
  name: string;
  index: number;
  base: THREE.Matrix4;
  /** The core's height when full. */
  full: number;
  extractor: MachineView;
  shown: number;
  /** What turns while it draws, paused while it is full. */
  turning: Spin | null;
}

export interface Fills {
  clear(): void;
  /** A newer view of the same extractors (someone collected): the cores follow it. */
  track(view: PlanetView): void;
  /** Fills each core to how full its extractor is at server time `time` (null: as its state says); true while one changed. */
  animate(time: number | null): boolean;
}

const _c = new THREE.Vector3();
const _p = new THREE.Vector3();
const _s = new THREE.Vector3(1, 1, 1);
const _f = new THREE.Matrix4();
const _q = new THREE.Quaternion();
const _q2 = new THREE.Quaternion();
const _base = new THREE.Matrix4();
const _t = new THREE.Matrix4();
const _m = new THREE.Matrix4();

const coreMatrix = (
  base: THREE.Matrix4,
  fill: number,
  full: number,
  out: THREE.Matrix4,
): THREE.Matrix4 => out.copy(base).multiply(_t.makeScale(1, Math.max(EMPTY, fill) * full, 1));

export function createFills(instances: Instances): Fills & {
  add(fill: Omit<Fill, 'shown'>): void;
} {
  const fills: Fill[] = [];
  return {
    clear() {
      fills.length = 0;
    },
    add(fill) {
      fills.push({ ...fill, base: fill.base.clone(), shown: -1 });
    },
    track(view) {
      for (const f of fills) {
        const extractor = view.machines?.get(f.tile);
        if (!extractor) continue;
        f.extractor = extractor;
        f.shown = -1;
      }
    },
    animate(time) {
      let changed = false;
      for (const f of fills) {
        const fill = Math.min(1, Math.max(0, f.extractor.fillAt(time ?? -Infinity)));
        if (f.turning) f.turning.paused = fill >= 1;
        if (Math.abs(fill - f.shown) < 0.002) continue;
        f.shown = fill;
        instances.setMatrixAt(f.name, f.index, coreMatrix(f.base, fill, f.full, _m));
        changed = true;
      }
      return changed;
    },
  };
}

/**
 * Where an extractor on tile `i` stands, `h0` above a planet of radius `r0`: in its tile's middle,
 * turned a little by the tile's own value. The placed props and the placing preview share it.
 */
export function extractorFrame(
  view: PlanetView,
  i: number,
  r0: number,
  h0: number,
  out: THREE.Matrix4,
): THREE.Matrix4 {
  centerOf(view, i, _c);
  _q.setFromUnitVectors(UP, _c);
  _q.multiply(_q2.setFromAxisAngle(UP, hash2(i, 4) * Math.PI * 2));
  _p.copy(_c).multiplyScalar(r0 + h0);
  return out.compose(_p, _q, _s);
}

/** A part's place in a frame: the frame moved by the part's offset. */
export const partAt = (
  frame: THREE.Matrix4,
  part: MachinePart,
  out: THREE.Matrix4,
): THREE.Matrix4 =>
  out.copy(frame).multiply(_f.makeTranslation(part.offset[0], part.offset[1], part.offset[2]));

/** The extractor on tile `i`, standing at height `h0`. Returns false when there is none. */
export function placeMachine(
  ins: Instances,
  spinners: Spinners,
  fills: ReturnType<typeof createFills>,
  view: PlanetView,
  i: number,
  h0: number,
): boolean {
  const extractor = view.machines?.get(i);
  // its level's look: an upgraded extractor looks its level
  const look = extractor ? lookOf(extractor.type, extractor.level) : undefined;
  if (!extractor || !look) return false;
  extractorFrame(view, i, ins.radius(), h0, _base);
  const at = (part: MachinePart): THREE.Matrix4 => partAt(_base, part, _m);
  for (const part of look.frame) ins.putMatrix(part.name, at(part));
  const { turning, core, coreFull } = look;
  const turns = ins.putMatrix(turning.name, at(turning));
  const spin =
    turns >= 0 && extractor.drawing && turning.spin
      ? spinners.add(turning.name, turns, _m, turning.spin, turning.speed)
      : null;
  const base = at(core).clone();
  const index = ins.putMatrix(core.name, coreMatrix(base, 0, coreFull, _m));
  if (index >= 0)
    fills.add({ tile: i, name: core.name, index, base, full: coreFull, extractor, turning: spin });
  return true;
}
